// Versiyon: 1.0.0 - Sipariş Görüntüleme Modalı (WaybillDisplayModal.tsx Tasarımı)
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Sipariş Kalemi arayüzü
interface OrderItem {
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  sctRate?: number;
  discountRate?: number;
  netTotal: number;
  grossTotal: number;
  lineNumber: number;
}

// Şirket/Şube bilgileri arayüzü
interface BranchInfoData {
  name: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
  bankAccounts?: Array<{bankName: string; iban: string;}>;
}

// Sipariş arayüzü
interface OrderData {
  id: string;
  orderNumber: string;
  orderType: string; // 'SALES_ORDER' | 'PURCHASE_ORDER'
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  orderDate: string;
  deliveryDate?: string;
  status: string;
  processStock: boolean;
  subtotal: number;
  vatTotal: number;
  sctTotal?: number;
  discountTotal?: number;
  grandTotal: number;
  currency: string;
  notes?: string;
  description?: string;
  paymentMethod?: string;
  iban?: string;
  paymentNote?: string;
  sourceOfferNumber?: string;
  linkedInvoiceNumber?: string;
  linkedWaybillNumber?: string;
  createdAt: string;
  updatedAt?: string;
  items: OrderItem[];
  branchInfo?: BranchInfoData;
}

interface OrderDisplayModalProps {
  open: boolean;
  onClose: () => void;
  order: OrderData | null;
  companyId: string | null;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '6px 8px',
  fontSize: '0.8rem',
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[200],
}));

const formatCurrency = (amount: number | undefined, currencyCode: string = 'TRY'): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  try {
    if (dateString.includes('T') && dateString.includes('Z')) {
      const [tarihKismi] = dateString.split('T');
      const [yil, ay, gun] = tarihKismi.split('-');
      return `${gun}.${ay}.${yil}`;
    }
    if (dateString.includes('-') && dateString.length === 10) {
      const [yil, ay, gun] = dateString.split('-');
      return `${gun}.${ay}.${yil}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(localDate);
  } catch (e) {
    console.error("Tarih formatı hatası:", e);
    return dateString;
  }
};

const formatDateTime = (dateTimeString: string | undefined): string => {
  if (!dateTimeString) return '-';
  try {
    if (dateTimeString.includes('T') && dateTimeString.includes('Z')) {
      const [_, saatKismi] = dateTimeString.split('T');
      const formatliSaat = saatKismi.split('.')[0];
      return formatliSaat;
    }
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.error("Saat formatı hatası:", e);
    return '-';
  }
};

const getStatusText = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'draft': 'Taslak',
    'pending': 'Beklemede',
    'confirmed': 'Onaylandı',
    'shipped': 'Sevk Edildi',
    'delivered': 'Teslim Edildi',
    'invoiced': 'Faturalandırıldı',
    'cancelled': 'İptal Edildi',
    'canceled': 'İptal Edildi',
  };
  return statusMap[status?.toLowerCase()] || status || '-';
};

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } = {
    'draft': 'default',
    'pending': 'warning',
    'confirmed': 'info',
    'shipped': 'primary',
    'delivered': 'success',
    'invoiced': 'secondary',
    'cancelled': 'error',
    'canceled': 'error',
  };
  return colorMap[status?.toLowerCase()] || 'default';
};

// Yazdırma stilleri
const printStyles = `
  @media print {
    body > *:not(.MuiDialog-root),
    .MuiDialog-root > *:not(.MuiDialog-container),
    .MuiDialog-container > *:not(.MuiPaper-root),
    .MuiDialog-paper > *:not(.MuiDialogContent-root),
    .MuiDialogTitle-root,
    .MuiDialogActions-root {
      display: none !important;
    }

    .MuiDialog-root,
    .MuiDialog-container,
    .MuiDialog-paper,
    .MuiDialogContent-root {
      display: block !important;
      width: auto !important;
      height: auto !important;
      overflow: visible !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    #printableOrderContent {
      width: 210mm !important; 
      min-height: 290mm !important;
      padding: 10mm !important;
      margin: 0 auto !important;
      box-shadow: none !important;
      border: none !important;
      background-color: white !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      font-size: 10pt;
    }

    @page {
      size: A4;
      margin: 0;
    }

    #printableOrderContent .MuiPaper-root {
      box-shadow: none !important;
      border: 1px solid #ccc !important;
      background-color: white !important;
    }

    #printableOrderContent table {
      width: 100% !important;
      page-break-inside: auto;
    }
    #printableOrderContent tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    #printableOrderContent thead {
      display: table-header-group;
    }
  }
`;

const OrderDisplayModal: React.FC<OrderDisplayModalProps> = ({
  open,
  onClose,
  order,
  companyId,
}) => {
  const [fetchedBranchInfo, setFetchedBranchInfo] = useState<BranchInfoData | null>(null);
  const [loadingBranchInfo, setLoadingBranchInfo] = useState<boolean>(false);
  const [branchInfoError, setBranchInfoError] = useState<string | null>(null);
  
  // Firma bilgilerini yükleme
  useEffect(() => {
    if (open && companyId) {
      setLoadingBranchInfo(true);
      setBranchInfoError(null);
      setFetchedBranchInfo(null);
      
      getCompanyInfo(companyId)
        .then((info) => {
          if (info) {
            setFetchedBranchInfo(info as BranchInfoData);
          } else {
            setBranchInfoError("Firma bilgileri bulunamadı.");
            setFetchedBranchInfo({ name: 'Firma Bilgileri Yok' });
          }
        })
        .catch((err) => {
          console.error("Firma bilgileri yüklenirken hata:", err);
          setBranchInfoError("Firma bilgileri yüklenirken bir hata oluştu.");
          setFetchedBranchInfo({ name: 'Hata oluştu' });
        })
        .finally(() => {
          setLoadingBranchInfo(false);
        });
    } else {
      setFetchedBranchInfo(null);
      setLoadingBranchInfo(false);
      setBranchInfoError(null);
    }
  }, [open, companyId]);
  
  if (!order) {
    return null;
  }

  const items = order.items || [];
  const accountInfo = order.account;
  const isSalesOrder = order.orderType === 'SALES_ORDER';
  const orderTypeLabel = isSalesOrder ? 'SATIŞ SİPARİŞİ' : 'ALIŞ SİPARİŞİ';

  const hasDiscount = items.some(item => typeof item.discountRate === 'number' && item.discountRate > 0);
  const hasSct = items.some(item => typeof item.sctRate === 'number' && item.sctRate > 0);

  // PDF olarak indirme fonksiyonu
  const handleDownloadPdf = () => {
    const printableContent = document.getElementById('printableOrderContent');
    if (!printableContent) return;

    html2canvas(printableContent, { 
      scale: 2, 
      logging: false,
      useCORS: true,
      allowTaint: true
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Siparis_${order.orderNumber}.pdf`);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <GlobalStyles styles={printStyles} />
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', p: 1.5, px: 2.5 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {orderTypeLabel}
          </Typography>
          <Chip
            label={getStatusText(order.status)}
            size="small"
            color={getStatusColor(order.status)}
            sx={{ 
              color: 'white',
              fontWeight: 'bold',
              '& .MuiChip-label': { color: 'white' }
            }}
          />
        </Grid>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: '#f9f9f9' }}>
        <Box id="printableOrderContent" sx={{ p: '20px', m:'auto', width: '210mm', minHeight: '297mm', backgroundColor: 'white', boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
          {/* Sipariş Başlığı ve Numarası */}
          <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
            <Grid item xs={7}>
              {loadingBranchInfo ? (
                <CircularProgress size={20} />
              ) : fetchedBranchInfo ? (
                <>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                    {fetchedBranchInfo.name}
                  </Typography>
                  {fetchedBranchInfo.address && (
                    <Typography variant="body2" display="block">
                      {fetchedBranchInfo.address}
                    </Typography>
                  )}
                  {(fetchedBranchInfo.taxOffice || fetchedBranchInfo.taxNumber) && (
                    <Typography variant="body2" display="block">
                      <strong>Vergi Dairesi:</strong> {fetchedBranchInfo.taxOffice || '-'} / <strong>V.K.N:</strong> {fetchedBranchInfo.taxNumber || '-'}
                    </Typography>
                  )}
                  {(fetchedBranchInfo.phone || fetchedBranchInfo.email) && (
                    <Typography variant="body2" display="block">
                      Tel: {fetchedBranchInfo.phone || '-'} {fetchedBranchInfo.email && `| E-posta: ${fetchedBranchInfo.email}`}
                    </Typography>
                  )}
                  {(fetchedBranchInfo.mersisNo || fetchedBranchInfo.tradeRegistryNo) && (
                    <Typography variant="body2" display="block">
                      Mersis No: {fetchedBranchInfo.mersisNo || '-'} | Tic. Sic. No: {fetchedBranchInfo.tradeRegistryNo || '-'}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="error">{branchInfoError || "Firma bilgileri yüklenemedi."}</Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'right', border: '1px solid #eee', p: 1, borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>SİPARİŞ</Typography>
              <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1.1rem' }}>
                {order.orderNumber}
              </Typography>
              <Divider sx={{ my: 0.5 }}/>
              <Typography variant="body2"><strong>Sipariş Tarihi:</strong> {formatDate(order.orderDate)}</Typography>
              {order.deliveryDate && (
                <Typography variant="body2"><strong>Teslimat Tarihi:</strong> {formatDate(order.deliveryDate)}</Typography>
              )}
              <Typography variant="body2"><strong>Düzenleme Saati:</strong> {formatDateTime(order.createdAt)}</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip 
                  label={getStatusText(order.status)} 
                  size="small" 
                  color={getStatusColor(order.status)}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Dönüşüm Bilgileri */}
          {(order.sourceOfferNumber || order.linkedInvoiceNumber || order.linkedWaybillNumber) && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#f0f8ff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: 'info.main' }}>
                DÖNÜŞÜM BİLGİLERİ
              </Typography>
              <Grid container spacing={2}>
                {order.sourceOfferNumber && (
                  <Grid item xs={4}>
                    <Typography variant="body2"><strong>Kaynak Teklif:</strong> {order.sourceOfferNumber}</Typography>
                  </Grid>
                )}
                {order.linkedInvoiceNumber && (
                  <Grid item xs={4}>
                    <Typography variant="body2"><strong>Bağlı Fatura:</strong> {order.linkedInvoiceNumber}</Typography>
                  </Grid>
                )}
                {order.linkedWaybillNumber && (
                  <Grid item xs={4}>
                    <Typography variant="body2"><strong>Bağlı İrsaliye:</strong> {order.linkedWaybillNumber}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Cari Bilgileri */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#fafafa' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {isSalesOrder ? 'MÜŞTERİ BİLGİLERİ' : 'TEDARİKÇİ BİLGİLERİ'}
            </Typography>
            
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{accountInfo.companyName}</Typography>
            {accountInfo.taxNumber && <Typography variant="body2"><strong>V.K.N / T.C. Kimlik No:</strong> {accountInfo.taxNumber}</Typography>}
            {accountInfo.taxOffice && <Typography variant="body2"><strong>Vergi Dairesi:</strong> {accountInfo.taxOffice}</Typography>}
            {accountInfo.address && <Typography variant="body2"><strong>Adres:</strong> {accountInfo.address}</Typography>}
            {accountInfo.phone && <Typography variant="body2"><strong>Telefon:</strong> {accountInfo.phone}</Typography>}
            {accountInfo.email && <Typography variant="body2"><strong>E-posta:</strong> {accountInfo.email}</Typography>}
          </Paper>

          {/* Ürün/Hizmet Tablosu */}
          {items && items.length > 0 ? (
            <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small" aria-label="sipariş kalemleri tablosu">
                <TableHead sx={{ backgroundColor: '#f0f0f0' }}>
                  <TableRow>
                    <StyledTableHeadCell sx={{ width: '30px' }}>#</StyledTableHeadCell>
                    <StyledTableHeadCell>Mal / Hizmet</StyledTableHeadCell>
                    <StyledTableHeadCell align="right" sx={{ width: '70px' }}>Miktar</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ width: '60px' }}>Birim</StyledTableHeadCell>
                    <StyledTableHeadCell align="right" sx={{ width: '100px' }}>Birim Fiyat</StyledTableHeadCell>
                    {hasDiscount && (
                      <StyledTableHeadCell align="right" sx={{ width: '60px' }}>İsk. (%)</StyledTableHeadCell>
                    )}
                    <StyledTableHeadCell align="right" sx={{ width: '60px' }}>KDV (%)</StyledTableHeadCell>
                    {hasSct && (
                      <StyledTableHeadCell align="right" sx={{ width: '60px' }}>ÖTV (%)</StyledTableHeadCell>
                    )}
                    <StyledTableHeadCell align="right" sx={{ width: '110px' }}>Tutar ({order.currency})</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <StyledTableCell component="th" scope="row">{item.lineNumber || index + 1}</StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: '500' }}>{item.itemName}</StyledTableCell>
                      <StyledTableCell align="right">{item.quantity.toLocaleString('tr-TR')}</StyledTableCell>
                      <StyledTableCell>{item.unit}</StyledTableCell>
                      <StyledTableCell align="right">{formatCurrency(item.unitPrice, order.currency).replace(order.currency, '').trim()}</StyledTableCell>
                      {hasDiscount && (
                        <StyledTableCell align="right">{item.discountRate || 0}%</StyledTableCell>
                      )}
                      <StyledTableCell align="right">{item.vatRate}%</StyledTableCell>
                      {hasSct && (
                        <StyledTableCell align="right">{item.sctRate || 0}%</StyledTableCell>
                      )}
                      <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(item.grossTotal, order.currency).replace(order.currency, '').trim()}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Bu siparişte ürün/hizmet kaydı bulunmamaktadır.</Typography>
            </Paper>
          )}

          {/* Toplamlar Bölümü */}
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={7} md={6} lg={5}>
              <TableContainer component={Paper} variant='outlined'>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <StyledTableCell>ARA TOPLAM</StyledTableCell>
                      <StyledTableCell align="right">{formatCurrency(order.subtotal, order.currency)}</StyledTableCell>
                    </TableRow>
                    {typeof order.discountTotal === 'number' && order.discountTotal > 0 && (
                      <TableRow>
                        <StyledTableCell sx={{ color: 'error.main' }}>TOPLAM İSKONTO</StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(order.discountTotal, order.currency)}</StyledTableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <StyledTableCell>TOPLAM KDV</StyledTableCell>
                      <StyledTableCell align="right">{formatCurrency(order.vatTotal, order.currency)}</StyledTableCell>
                    </TableRow>
                    {typeof order.sctTotal === 'number' && order.sctTotal > 0 && (
                      <TableRow>
                        <StyledTableCell>TOPLAM ÖTV</StyledTableCell>
                        <StyledTableCell align="right">{formatCurrency(order.sctTotal, order.currency)}</StyledTableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                      <StyledTableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>GENEL TOPLAM</StyledTableCell>
                      <StyledTableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{formatCurrency(order.grandTotal, order.currency)}</StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          {/* Ödeme Bilgileri */}
          {(order.paymentMethod || order.iban || order.paymentNote) && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>ÖDEME BİLGİLERİ</Typography>
              {order.paymentMethod && <Typography variant="body2"><strong>Ödeme Yöntemi:</strong> {order.paymentMethod}</Typography>}
              {order.iban && <Typography variant="body2"><strong>IBAN:</strong> {order.iban}</Typography>}
              {order.paymentNote && <Typography variant="body2"><strong>Ödeme Notu:</strong> {order.paymentNote}</Typography>}
            </Paper>
          )}

          {/* Notlar */}
          {order.notes && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>Sipariş Notları:</Typography>
              <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                {order.notes}
              </Typography>
            </Paper>
          )}

          {/* Stok İşleme Durumu */}
          {order.processStock && (
            <Box sx={{ mt: 2 }}>
              <Chip 
                label="Stok İşlendi" 
                size="small" 
                color="success" 
                variant="outlined"
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          
          {/* İmza Alanları */}
          <Grid container spacing={2} sx={{ mt: 4 }}>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 80, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {isSalesOrder ? 'SATICI' : 'ALICI'}
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <Typography variant="caption">İmza / Kaşe</Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 80, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {isSalesOrder ? 'MÜŞTERİ' : 'TEDARİKÇİ'}
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <Typography variant="caption">İmza / Kaşe</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          <Typography variant="caption" display="block" sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.7rem', mt: 2 }}>
            Bu sipariş formu elektronik ortamda düzenlenmiştir.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, backgroundColor: 'grey.100', borderTop: '1px solid #ddd' }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Kapat
        </Button>
        <Button 
          onClick={handleDownloadPdf} 
          variant="outlined" 
          color="primary"
          sx={{ mr: 1 }}
        >
          PDF İndir
        </Button>
        <Button 
          onClick={() => window.print()} 
          variant="contained" 
          color="secondary"
        >
          Yazdır
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDisplayModal;
export type { OrderData };
